import { SectionTitle, StyledInputLabel, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../../StyledComponents/Container";
import * as constants from "../../../../constants";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import CustomTextField from "../../../../shared/CustomTextFeild";
import { Box } from "@mui/system";
import { getCreditBalByPid, getCreditLineListDetails } from "../../services/CreditLine/CreditLineServices";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import { useTranslation } from "react-i18next";
import basestyle from "../../../CSS/Base.module.css";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import CustomAutocomplete from "../../../../shared/AutoComplete";
import { resetTableState, setPagination, setTableFilters } from "../../../../store/tableSlice";
import { notifyUser } from "../../Utils/notifyUser";
import { getClientDetailsByClientName } from "../../Utils/ClientSettingsTable";

interface Column {
    id: string;
    label: string;
    minWidth: number | string;
    align?: "center" | "left" | "right" | "undefined";
    isHyperlink: boolean;
}

const formatAmount = (
    amount: number | null | undefined,
    locale: string = "en-US",
    currency: string = "USD"
): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return formatter.format(amount ?? 0);
};


const CreditLineListingSummary = () => {
    const userInfo = useSelector((state: RootState) => state.auth);
    let role: any = userInfo.roles?.roleName
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const filters = useSelector((state: RootState) => state.table.creditLines?.filters || {});
    const pagination = useSelector((state: RootState) => state.table.creditLines);
    const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);

    const [page, setPage] = useState(pagination?.page || 1);
    const [rowsPerPage, setRowsPerPage] = useState(pagination?.pageSize || 5);
    const [listData, setListData] = useState([]);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [creditLine, setcreditLine] = useState(filters?.creditlineId || "");
    const [status, setStatus] = useState(filters?.status || null);
    const [creditBalance, setCreditBalance] = useState(0);
    const [clearTrigger, setClearTrigger] = useState(false);

    const handlePageChange = (newPage: number) => {
        setPage(newPage + 1);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    const handleIdClick = (rowData: any) => {
        navigate("/creditLineDetails", { state: rowData });
    };

    useEffect(() => {
        creditLineListing();
    }, [clearTrigger, status, page, rowsPerPage]);

    useEffect(() => {
        getCreditBalanceByPid();
    }, []);

    useEffect(() => {
        if (location.state?.message) {
            notifyUser(location.state.message, 'success', translate("creditLineSummary"));
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate]);

    async function creditLineListing(): Promise<void> {
        setIsLoading(true);
        const CreditLineFilterVO = {
            participantId: userInfo.defaultParticipantId,
            clientId: userInfo.clientId,
            creditlineId: creditLine || "",
            status: status === "All" ? "" : status,
            page: page,
            size: rowsPerPage,
        };

        try {
            dispatch(setTableFilters({
                tableKey: 'creditLines',
                filters: {
                    creditlineId: creditLine,
                    status: status
                }
            }));
            dispatch(setPagination({
                tableKey: 'creditLines',
                page: page,
                pageSize: rowsPerPage
            }));

            const response = await getCreditLineListDetails(CreditLineFilterVO)
                .catch((error: any) => {
                    if (error.status === constants.STATUS_CODES.NOT_FOUND) {
                        setIsLoading(false);
                        setListData([]);
                        setCount(0);
                    } else {
                        setIsLoading(false);
                        setListData([]);
                        setCount(0);
                        notifyUser(error.response?.data?.message, 'error', translate("creditLineSummary"));
                    }
                    return null;
                });

            if (response && response.status === constants.STATUS_CODES.SUCCESS) {
                const data = response.data.data;

                if (!data || data.length === 0) {
                    setCount(0);
                    setListData([]);
                    setIsLoading(false);
                    return;
                }

                const filterData = data.map((item: any) => {
                    const transactionHistory = item.creditlineTransactionHistory || [];
                    const firstAvailableCredit = transactionHistory[0]?.availableCredit ?? 0;
                    const lastAvailableCredit = transactionHistory[transactionHistory.length - 1]?.availableCredit ?? 0;

                    let creditPayment = "-";
                    const balance = item.status === constants.IS_Status_ACTIVE
                        ? (firstAvailableCredit - lastAvailableCredit)
                        : (firstAvailableCredit - (ClientDetails?.name === constants.CLIENT_NAME_DC_GARY ? lastAvailableCredit : item.availableCredit || 0));
                    if (item.status === constants.IS_Status_ACTIVE) {
                        creditPayment = ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_NON_DNL
                            ? translate('paymentWithdrawal') : (ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_DNL &&
                                (firstAvailableCredit - lastAvailableCredit) > 0 ? translate('makePayment') : "-");
                    }
                    else if (item.status === constants.IS_Status_INACTIVE && firstAvailableCredit - (item.availableCredit || 0) > 0) {
                        creditPayment = ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_NON_DNL && balance > 0 ? translate('paymentWithdrawal') :
                            (ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_DNL &&
                                (firstAvailableCredit - lastAvailableCredit) > 0 ? translate('makePayment') : "-");
                    }
                    const outstandingBalance = formatAmount(balance);

                    const availableCredit = formatAmount(ClientDetails?.name === constants.CLIENT_NAME_DC_GARY ? lastAvailableCredit : item.availableCredit || 0);

                    return {
                        ...item,
                        availableCredit: availableCredit,
                        dueDate: "-",
                        creditPayment,
                        outstandingBalance,
                    };
                });

                setCount(response.data.totalRecordCount || 0);
                setListData(filterData);
                setIsLoading(false);
            } else {
                setCount(0);
                setListData([]);
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error fetching credit line data:", error);
            setIsLoading(false);
        }
    }

    async function getCreditBalanceByPid() {
        try {
            const response = await getCreditBalByPid(userInfo.defaultParticipantId, userInfo.clientId);

            if (response.status === constants.STATUS_CODES.SUCCESS) {
                const filteredData = response.data.data.glNonAssets.filter((item: any) => item.glAcctName === 'Credit Line');
                setCreditBalance(filteredData[0]?.glAcctBalance || 0);
            }
        } catch (error) {
            console.error("Error fetching credit balance:", error);
        }
    }

    async function handleSearch() {
        setPage(1);
        setRowsPerPage(5);
        creditLineListing();
    }


    function handleClear() {
        setcreditLine("");
        setStatus("All");
        setPage(1);
        setRowsPerPage(5);
        setClearTrigger(prev => !prev);

        dispatch(resetTableState({ tableKey: 'creditLines' }));
    }

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const restrictedRoles = [
        ...(ClientDetails?.name !== constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_FINANCIAL_ANALYST] : []),
        constants.ROLES_READ_ONLY_USER,
        constants.ROLES_BUDGET_ANALYST,
        constants.ROLES_PROJECT_APPROVER,
        constants.ROLES_READ_ONLY_ALL_LOCATIONS,
    ];

    const Columns: any = [
        { id: "creditlineId", label: translate('creditLine#'), width: "12%", align: "center", isHyperlink: true },
        { id: "name", label: translate('name'), width: "18%", align: "left", isHyperlink: false },
        ...(ClientDetails?.newCreditLineProjectNumber === constants.IS_ACTIVE_YES
            ? [{ id: "projectNumber", label: translate("projectNumber"), minWidth: "15%", align: "left", isHyperlink: false }]
            : []),
        { id: "description", label: translate('creditLineDesc'), width: "20%", align: "left", isHyperlink: false },  // wider for description
        { id: "outstandingBalance", label: translate('outstandingBalance'), width: "15%", align: "right", isHyperlink: false },
        { id: "availableCredit", label: translate('availableCredit'), width: "12%", align: "right", isHyperlink: false },
        { id: "status", label: translate('status'), width: "8%", align: "center", isHyperlink: false },
        ...(!restrictedRoles.includes(role)
            ? [{ id: "creditPayment", label: translate('action'), width: "15%", align: "center", isHyperlink: true }]
            : []),
    ];


    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle sx={{ display: 'flex', justifyContent: 'space-between', mr: 1.5 }}>
                        <span>{translate('creditLineSummary')}</span>
                        <span>
                            <StyledInputLabel sx={{ fontSize: '20px' }}>
                                {translate('creditLineBalance')}:<span style={{ color: 'red' }}> {formatAmount(creditBalance)}</span>
                            </StyledInputLabel>
                        </span>
                    </SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        <Box className="search-fields-container">
                            <Box className="left-fields">
                                <CustomTextField
                                    sx={{ width: { xs: "100%", sm: 250 } }}
                                    label={translate('creditLine')}
                                    value={creditLine}
                                    onChange={(e) => setcreditLine(e.target.value.trim())}
                                    onKeyDown={handleKeyPress}
                                    inputProps={{ maxLength: 15 }}
                                />
                                <CustomAutocomplete
                                    sx={{ width: { xs: "100%", sm: 250 } }}
                                    options={["All", constants.IS_Status_ACTIVE, constants.IS_Status_INACTIVE]}
                                    value={status}
                                    onChange={(event, newValue) => {
                                        if (newValue !== null) {
                                            setStatus(newValue);
                                            setPage(1);
                                            setRowsPerPage(5);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!status) {
                                            setStatus("All");
                                        }
                                    }}
                                    label={translate('status')}
                                />
                                <ContainedButton onClick={handleSearch} disabled={isLoading}>
                                    {translate('search')}
                                </ContainedButton>
                                <OutlinedButton onClick={handleClear} disabled={isLoading}>
                                    {translate('clear')}
                                </OutlinedButton>
                            </Box>
                            {![
                                ...(ClientDetails?.name !== constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_FINANCIAL_ANALYST] : []),
                                constants.ROLES_BUDGET_ANALYST,
                                constants.ROLES_PARTICIPANT_USER,
                                constants.ROLES_READ_ONLY_USER,
                                constants.ROLES_PROJECT_APPROVER,
                                constants.ROLES_READ_ONLY_ALL_LOCATIONS,
                            ].includes(role) && (
                                    <Box className="right-fields">
                                        <ContainedButton onClick={() => navigate("/newCreditLine")}>
                                            <ControlPointIcon />
                                            {translate('creditLines')}
                                        </ContainedButton>
                                    </Box>
                                )}
                        </Box>
                        <ReusableTable
                            tableKey="creditLineListing"
                            columns={Columns}
                            rows={listData}
                            count={count}
                            page={page - 1}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            onRowClick={handleIdClick}
                            loading={isLoading}
                        />
                        <div className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={() => navigate("/summary")}>
                                {translate('back')}
                            </ContainedButton>
                        </div>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    )
}

export default CreditLineListingSummary;